// Usuarios (mantenedor, Módulo B) sobre TanStack Query. Mismo patrón que
// `hooks/useAuth.ts`: la cache de la query es la única fuente de verdad de la
// lista; las mutaciones invalidan esa key para refrescarla.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  actualizarUsuarioRequest,
  cambiarAccesoRequest,
  crearUsuarioRequest,
  fetchUsuarios,
  reenviarInvitacionRequest,
  type ActualizarUsuarioInput,
  type CrearUsuarioInput,
} from "@/lib/usuarios";

export const USUARIOS_KEY = ["usuarios"] as const;

export function useUsuarios() {
  return useQuery({
    queryKey: USUARIOS_KEY,
    queryFn: fetchUsuarios,
  });
}

export function useCrearUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearUsuarioInput) => crearUsuarioRequest(input),
    onSuccess: () => {
      // A diferencia de useLogin (que siembra la cache), acá invalidamos:
      // el 201 devuelve el usuario creado pero no la lista paginada/ordenada
      // completa, así que un refetch es más simple y correcto.
      queryClient.invalidateQueries({ queryKey: USUARIOS_KEY });
    },
  });
}

export function useActualizarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: ActualizarUsuarioInput & { id: number }) =>
      actualizarUsuarioRequest(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USUARIOS_KEY }),
  });
}

/**
 * Conmutador de acceso. Invalida en vez de parchear la fila: la respuesta del
 * PATCH no carga la relación `perfil`, así que escribirla en la cache dejaría
 * la columna Perfil vacía en la fila que se acaba de tocar.
 */
export function useCambiarAcceso() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, acceso }: { id: number; acceso: boolean }) =>
      cambiarAccesoRequest(id, acceso),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USUARIOS_KEY }),
  });
}

export function useReenviarInvitacion() {
  return useMutation({
    mutationFn: (id: number) => reenviarInvitacionRequest(id),
  });
}
